/* eslint-disable */
import {
  Collapse,
  FormInstance,
  Image,
  Typography,
  Space,
  Button,
  Tooltip,
  Select,
  Input,
  DatePicker,
  Tag,
  Spin,
} from 'antd';
import { useState, useEffect } from 'react';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  CopyOutlined,
  EditOutlined,
  TagsOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { InputInterface } from '../utils/forms/FormInterface';
import { fallback } from '../constants/FallbackImage';
import UserDetails from './UserDetails';
import PaymentDetails from './PaymentDetails';
import {
  PARTNER_DOCUMENT_ACCEPT_REJECT,
  PARTNER_ORDER_PROCESSED_PDF,
  PARTNER_MEMBER_ADDITIONAL_DETAILS,
} from '../constants/EntityName';
import { partnerMemberStatusOptions } from '../constants/AppConstant';
import callApi, { uploadImageApiCall } from '../Api/config';
import { toastError, toastSuccess } from '../service/Toast';
import { BASE_IMAGE_URL, BASE_API_URL } from '../env';
import { getToken } from '../service/CookieManager';
import PdfViewer from './PdfViewer';
import PhotoRemarksModal from './PhotoRemarksModal';
import moment from 'moment';
import { InvertFillButton } from './InvertFillButton';
import { Download, CircleCheckBig, CircleX, X } from 'lucide-react';

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface ReviewPartnerOrderMemberProps {
  formInput: InputInterface;
  index: number;
  form: FormInstance;
  isCreateMode?: boolean;
  additionalData?: any;
  formDetails?: any;
  showOnlyForm?: boolean;
  showOnlyDocuments?: boolean;
  showOnlyProcessed?: boolean;
  showOnlyAdditionalDetails?: boolean;
  hideApplicationStatus?: boolean;
  /** Callback when member status is updated - syncs parent state so only that member is updated */
  onMemberStatusUpdated?: (memberId: string, status: string) => void;
}

const ReviewPartnerOrderMember = (props: ReviewPartnerOrderMemberProps) => {
  const { formDetails: member } = props;
  const [memberData, setMemberData] = useState<any>(member);
  const [loading, setLoading] = useState(false);
  const [templateAttempted, setTemplateAttempted] = useState(false);
  const [openAddRemark, setOpenAddRemarks] = useState<boolean>(false);
  const [activePhoto, setActivePhoto] = useState<any>({});
  const [additionalDetailsState, setAdditionalDetailsState] = useState({
    referenceNumber: member?.referenceNumber || '',
    passportNumber: member?.passportNumber || '',
    dateOfBirth: member?.dateOfBirth ? moment(member.dateOfBirth) : null,
    trackingLink: member?.trackingLink || '',
  });
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setMemberData(member);
    setTemplateAttempted(false);
    setAdditionalDetailsState({
      referenceNumber: member?.referenceNumber || '',
      passportNumber: member?.passportNumber || '',
      dateOfBirth: member?.dateOfBirth ? moment(member.dateOfBirth) : null,
      trackingLink: member?.trackingLink || '',
    });
  }, [member]);

  useEffect(() => {
    const formDataEntry = memberData?.formEntries?.[0];
    const hasSteps = formDataEntry?.formData?.steps;

    if (!hasSteps && !templateAttempted && memberData?.group?.packageId) {
      const fetchTemplate = async () => {
        setTemplateAttempted(true);
        try {
          const packageId = memberData.group.packageId;
          const ageGroup = memberData.ageGroup === 'Minor' ? 'Minor' : 'Adult';
          const country = memberData.group.applyingFromCountry;

          let url = `/admin/forms/package/${packageId}?category=${ageGroup}`;
          if (country) url += `&country=${encodeURIComponent(country)}`;

          const res: any = await callApi(url, 'GET');

          if (res?.data?.data?.steps) {
            const steps = res.data.data.steps;
            const answers = formDataEntry?.formData || {};

            const processQuestions = (questions: any[]): any[] => {
              const result: any[] = [];
              (questions || []).forEach(sq => {
                const questionId = sq.question?.id || sq.id;
                const sqWithAnswer = {
                  ...sq,
                  question: {
                    ...sq.question,
                    value: answers[questionId] || null,
                  },
                };
                result.push(sqWithAnswer);

                // Check for dependent questions in options
                if (sq.isDependent && sq.options) {
                  sq.options.forEach((opt: any) => {
                    const depQuestions =
                      opt.questions || opt.dependentQuestions || opt.dependent_questions || [];
                    const depQuestionsArray = Array.isArray(depQuestions)
                      ? depQuestions
                      : [depQuestions];

                    // Recursively process dependent questions
                    // We only include them if their parent's answer matches this option
                    // OR if we want to show all answers that were provided
                    const optValue = typeof opt === 'string' ? opt : opt.value || opt.label || opt;
                    const currentAnswer = answers[questionId];

                    if (
                      String(optValue || '')
                        .trim()
                        .toLowerCase() ===
                      String(currentAnswer || '')
                        .trim()
                        .toLowerCase()
                    ) {
                      const processedDeps = processQuestions(
                        depQuestionsArray.map(dq =>
                          dq.question ? dq : { question: dq, id: dq.id, isRequired: dq.isRequired }
                        )
                      );
                      result.push(...processedDeps);
                    }
                  });
                }
              });
              return result;
            };

            const enrichedSteps = steps.map((step: any) => ({
              ...step,
              stepQuestions: processQuestions(step.stepQuestions || []),
            }));

            const newMemberData = JSON.parse(JSON.stringify(memberData));
            if (!newMemberData.formEntries) newMemberData.formEntries = [];
            if (!newMemberData.formEntries[0]) newMemberData.formEntries[0] = { formData: {} };
            newMemberData.formEntries[0].formData.steps = enrichedSteps;

            setMemberData(newMemberData);
          }
        } catch (err) {
          console.error('Error fetching form template in CMS:', err);
        }
      };
      fetchTemplate();
    }
  }, [memberData, templateAttempted]);

  const onDownload = async (fileUrl: any) => {
    if (fileUrl) {
      window.open(
        fileUrl?.startsWith('https') || !fileUrl ? fileUrl : `${BASE_IMAGE_URL}/${fileUrl}`
      );
    }
  };

  // Helper to group documents by type
  const getGroupedDocuments = () => {
    if (!memberData?.documents) return {};
    return memberData.documents.reduce((acc: any, doc: any) => {
      const type = doc.documentType || 'Others';
      if (!acc[type]) acc[type] = [];
      acc[type].push(doc);
      return acc;
    }, {});
  };

  const getMemberFormData = (downloadFormButton?: React.ReactNode) => {
    const formDataEntry = memberData?.formEntries?.[0];
    const steps = formDataEntry?.formData?.steps;

    const group = memberData?.group;
    const applyingFrom = group?.applyingFromCountry;
    const citizenship = memberData?.citizenship;
    const destination = memberData?.destination;
    const packageName = group?.partnerPackage?.package?.name;
    const serviceName = group?.partnerPackage?.name;
    const NAVY = 'black';

    const PrintField = ({ label, value }: { label: string; value?: React.ReactNode }) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <span
          style={{
            fontSize: '15px',
            fontWeight: 400,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#53657f',
            lineHeight: 1.2,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#0f172a',
            lineHeight: 1.35,
          }}
        >
          {value || '—'}
        </span>
      </div>
    );

    const orderCardStyle: React.CSSProperties = {
      width: '100%',
      borderRadius: '10px',
      border: '1px solid #e2e8f0',
      background: '#fff',
      padding: '18px 20px',
      marginBottom: '18px',
    };

    const fieldGridStyle: React.CSSProperties = {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      columnGap: '28px',
      rowGap: '14px',
    };

    const stepHeaderStyle: React.CSSProperties = {
      fontSize: '15px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: NAVY,
      marginBottom: '14px',
      paddingBottom: '10px',
      lineHeight: 1.2,
    };

    const qaRowStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '10px 0',
      borderBottom: '1px solid #f1f5f9',
      alignItems: 'flex-start',
    };

    const qaLabelStyle: React.CSSProperties = {
      fontSize: '16px',
      fontWeight: 400,
      color: '#475569',
      lineHeight: 1.5,
      paddingTop: '2px',
    };

    const qaValueStyle: React.CSSProperties = {
      fontSize: '15px',
      fontWeight: 600,
      color: '#0f172a',
      lineHeight: 1.5,
    };

    const imageWrapStyle: React.CSSProperties = {
      display: 'inline-flex',
      position: 'relative',
      borderRadius: '6px',
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
    };

    const hasTopFields = applyingFrom || citizenship || destination || serviceName;

    return (
      <>
        <div style={orderCardStyle}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '10px',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: NAVY,
                lineHeight: 1.2,
              }}
            >
              Order &amp; Member Information
            </div>
            {downloadFormButton}
          </div>
          <div style={fieldGridStyle}>
            <PrintField label="Order ID" value={group?.groupCode} />
            <PrintField label="Member Name" value={memberData?.name} />
            <PrintField label="Age Group" value={memberData?.ageGroup} />
            <PrintField label="Package Name" value={packageName} />
          </div>
          {hasTopFields && (
            <>
              <div
                style={{
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px dashed #cbd5e1',
                }}
              />
              <div style={fieldGridStyle}>
                {applyingFrom && <PrintField label="Applying From" value={applyingFrom} />}
                {citizenship && <PrintField label="Citizenship" value={citizenship} />}
                {destination && <PrintField label="Destination" value={destination} />}
                {serviceName && <PrintField label="Service Name" value={serviceName} />}
              </div>
            </>
          )}
        </div>

        {!steps ? (
          <div style={{ padding: '20px', color: '#64748b' }}>
            No form data available for this member.
          </div>
        ) : (
          steps?.map((step: any, stepIdx: number) => {
            const questions = step?.stepQuestions || [];
            return (
              <div
                key={step?.id}
                style={{ width: '100%', marginBottom: '18px', padding: '0 20px' }}
              >
                <div style={stepHeaderStyle}>
                  {String(stepIdx + 1).padStart(2, '0')}. {step?.stepTitle}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {questions?.map((question: any, qIdx: number) => (
                    <div
                      key={question?.id}
                      style={{
                        ...qaRowStyle,
                        borderBottom:
                          qIdx === (questions?.length || 0) - 1 ? 'none' : '1px solid #f1f5f9',
                      }}
                    >
                      <div style={qaLabelStyle}>
                        <span style={{ color: NAVY }}>Q</span> {question?.question?.label}
                      </div>
                      <div>
                        {question?.question?.inputType === 'file upload' ||
                        question?.question?.inputType === 'image upload' ? (
                          <div style={imageWrapStyle}>
                            <Image
                              width={200}
                              src={
                                question?.question?.value?.url?.startsWith('https') ||
                                !question?.question?.value?.url
                                  ? question?.question?.value?.url
                                  : `${BASE_IMAGE_URL}/${question?.question?.value?.url}`
                              }
                              fallback={fallback}
                            />
                            <DownloadOutlined
                              style={{
                                position: 'absolute',
                                fontSize: 16,
                                bottom: '8px',
                                right: '8px',
                                padding: '4px',
                                cursor: 'pointer',
                                backgroundColor: 'rgba(0, 0, 0, 0.60)',
                                borderRadius: '4px',
                                color: 'white',
                              }}
                              onClick={() => onDownload(question?.question?.value?.url)}
                            />
                          </div>
                        ) : question?.question?.inputType === 'date' ? (
                          <span style={qaValueStyle}>
                            {(() => {
                              const d = question?.question?.value;
                              if (!d) return '—';
                              const sd = new Date(d);
                              const day = sd.getDate();
                              const monthName = sd.toLocaleString('default', {
                                month: 'short',
                              });
                              const year = sd.getFullYear();
                              return `${day}/${monthName}/${year}`;
                            })()}
                          </span>
                        ) : (
                          <span style={qaValueStyle}>{question?.question?.value || '—'}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </>
    );
  };

  const handleMergeAllDocuments = async () => {
    if (!memberData?.documents || memberData.documents.length === 0) {
      toastError('No documents available to merge');
      return;
    }

    try {
      toastSuccess('Merging documents... This may take a moment.');

      // Group documents by documentName/type
      const documentsByType: Array<{
        documentId: string;
        documentName: string;
        documents: Array<{ url: string; fileName: string; photoId: string }>;
      }> = [];

      const groupedDocs = getGroupedDocuments();
      Object.entries(groupedDocs).forEach(([docType, docs]) => {
        const docsArray = Array.isArray(docs) ? docs : [];
        const docArray = docsArray.map((doc: any) => ({
          url: doc.s3Key || doc.url || '',
          fileName: doc.documentName || docType,
          photoId: doc.id || '',
        }));

        if (docArray.length > 0) {
          documentsByType.push({
            documentId: docsArray[0]?.id || '',
            documentName: docType,
            documents: docArray,
          });
        }
      });

      if (documentsByType.length === 0) {
        toastError('No documents available to merge');
        return;
      }

      // Call backend PDF merge API
      const token = getToken('/pdf/merge-all-combined');
      const response = await fetch(`${BASE_API_URL}/pdf/merge-all-combined`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          memberId: memberData.id,
          documents: documentsByType,
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${memberData.name.replace(/\s+/g, '_')}_All_Documents.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toastSuccess('Documents merged and downloaded successfully');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to merge documents');
      }
    } catch (error: any) {
      console.error('Merge documents error:', error);
      toastError(error?.response?.data?.message || 'Failed to merge documents. Please try again.');
    }
  };

  const handleDownloadAll = () => {
    if (!memberData?.documents || memberData.documents.length === 0) {
      toastError('No documents available to download');
      return;
    }

    const allUrls: any[] = [];
    memberData.documents.forEach((doc: any) => {
      const url = doc.s3Key || doc.url;
      if (url) {
        allUrls.push({
          url: url,
          fileName: doc.documentName || 'document',
        });
      }
    });

    const downloadWithDelay = async (url: string, filename: string) => {
      const a = document.createElement('a');
      a.href = url?.startsWith('https') || !url ? url : `${BASE_IMAGE_URL}/${url}`;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      await new Promise(resolve => setTimeout(resolve, 1000));
    };

    const getFileExtension = (url: string) => {
      const parts = url.split('.');
      return parts[parts.length - 1].toLowerCase();
    };

    const downloadAll = async () => {
      for (let i = 0; i < allUrls.length; i++) {
        const item = allUrls[i];
        if (item.url) {
          try {
            let filename = item.fileName || `file${i + 1}`;
            const extension = getFileExtension(item.url);
            if (
              extension === 'jpg' ||
              extension === 'png' ||
              extension === 'pdf' ||
              extension === 'jpeg'
            ) {
              if (!filename.toLowerCase().endsWith(`.${extension}`)) {
                filename += `.${extension}`;
              }
            }
            await downloadWithDelay(item.url, filename);
          } catch (error) {
            console.error('Download failed for:', item.url, error);
          }
        }
      }
    };

    downloadAll();
    toastSuccess('Downloading all documents...');
  };

  const handleAddRemarks = (photo: any) => () => {
    setActivePhoto(photo);
    setOpenAddRemarks(true);
  };

  const handleRemarkSuccess = (photoId: string, remark: string) => {
    const updatedDocs = memberData.documents.map((doc: any) =>
      doc.id === photoId ? { ...doc, remark } : doc
    );
    setMemberData({ ...memberData, documents: updatedDocs });
  };

  const getMemberDocuments = () => {
    const groupedDocs = getGroupedDocuments();
    const types = Object.keys(groupedDocs);

    if (types.length === 0) {
      return <div>No documents uploaded.</div>;
    }

    return (
      <div>
        {types.map(type => (
          <div key={type} style={{ marginBottom: '25px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '30px',
                marginBottom: '12px',
                paddingBottom: '8px',
              }}
            >
              <Title
                level={5}
                style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'black' }}
              >
                {type}
              </Title>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: '12px',
              }}
            >
              {groupedDocs[type].map((doc: any) => {
                const docUrl = doc.url || doc.s3Key;
                const urlPath = docUrl?.split('?')[0] || '';
                const isPdf = urlPath.slice(-3) === 'pdf' || urlPath.slice(-4) === '.pdf';
                const status = doc.status?.toLowerCase();
                const isAccepted = status === 'accepted';
                const isRejected = status === 'rejected';

                return (
                  <div
                    key={doc.id}
                    id={`${doc.id}-accept-reject`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      padding: '10px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      backgroundColor: isAccepted
                        ? 'lightgreen'
                        : isRejected
                          ? 'salmon'
                          : doc.remark
                            ? '#fff6e0'
                            : 'white',
                      minHeight: '280px',
                      position: 'relative',
                    }}
                  >
                    {isAccepted || isRejected ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: '14px',
                          left: '14px',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'white',
                          backgroundColor: isAccepted ? '#28a745' : '#dc3545',
                          zIndex: 10,
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        {isAccepted ? 'Accepted' : 'Rejected'}
                      </div>
                    ) : null}

                    <div
                      style={{
                        height: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8fafc',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}
                    >
                      {isPdf ? (
                        <PdfViewer photo={docUrl} />
                      ) : (
                        <Image
                          width={200}
                          height={180}
                          style={{ objectFit: 'contain' }}
                          src={
                            docUrl?.startsWith('https') || !docUrl
                              ? docUrl
                              : `${BASE_IMAGE_URL}/${docUrl}`
                          }
                          fallback={fallback}
                        />
                      )}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr 1fr 1fr',
                          gap: '8px',
                        }}
                      >
                        <Tooltip
                          title={
                            doc.remark
                              ? `Current remarks: ${doc.remark}`
                              : 'Add remarks to this document'
                          }
                          placement="topLeft"
                          overlayStyle={{
                            maxWidth: '300px',
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          <Button
                            size="small"
                            style={{
                              backgroundColor: '#586f80',
                              color: 'white',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              fontSize: '13px',
                              height: '28px',
                              width: '100%',
                            }}
                            onClick={handleAddRemarks(doc)}
                          >
                            {doc.remark ? 'Edit Remark' : 'Add Remark'}
                          </Button>
                        </Tooltip>

                        <Tooltip placement="top" title={'Accept'}>
                          <Button
                            size="small"
                            style={{
                              color: 'black',
                              border: 'none',
                              backgroundColor: 'transparent',
                              padding: '3px 8px',
                              fontSize: '13px',
                              height: '28px',
                              width: '100%',
                            }}
                            onClick={() => handleDocumentAcceptReject(doc.id, true)}
                          >
                            <CircleCheckBig size={20} />
                          </Button>
                        </Tooltip>
                        <Tooltip placement="bottom" title={'Reject'}>
                          <Button
                            size="small"
                            style={{
                              color: 'black',
                              border: 'none',
                              backgroundColor: 'transparent',
                              padding: '3px 8px',
                              fontSize: '13px',
                              height: '28px',
                              width: '100%',
                            }}
                            onClick={() => handleDocumentAcceptReject(doc.id, false)}
                          >
                            <CircleX size={20} />
                          </Button>
                        </Tooltip>
                        <Tooltip placement="top" title={'Download'}>
                          <Button
                            size="small"
                            style={{
                              color: 'black',
                              border: 'none',
                              backgroundColor: 'transparent',
                              padding: '3px 8px',
                              fontSize: '13px',
                              height: '28px',
                              width: '100%',
                            }}
                            onClick={() => onDownload(doc.url || doc.s3Key)}
                          >
                            <Download size={20} />
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div
          style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}
        >
          <InvertFillButton
            variant="blue"
            size="sm"
            fontSize="base"
            fontWeight="medium"
            onClick={handleMergeAllDocuments}
          >
            <Download size={16} /> Merge & Download All
          </InvertFillButton>
          <InvertFillButton
            variant="white"
            size="sm"
            fontSize="base"
            fontWeight="medium"
            onClick={handleDownloadAll}
          >
            <Download size={16} /> Download All Documents
          </InvertFillButton>
        </div>

        <PhotoRemarksModal
          formDetails={memberData}
          openAddRemark={openAddRemark}
          setOpenAddRemarks={setOpenAddRemarks}
          activePhoto={activePhoto}
          entityName={PARTNER_MEMBER_ADDITIONAL_DETAILS}
          onSuccess={handleRemarkSuccess}
        />
      </div>
    );
  };

  const handleUpdateMemberInfo = async (field: string, value: any) => {
    try {
      const res = await callApi(`${PARTNER_MEMBER_ADDITIONAL_DETAILS}/${memberData.id}`, 'PATCH', {
        [field]: value,
      });
      if (res?.status || res?.data?.statusCode === 200) {
        toastSuccess(`${field} updated`);
        setMemberData({ ...memberData, [field]: value });
      } else {
        toastError(`Failed to update ${field}`);
      }
    } catch (error) {
      toastError(`Failed to update ${field}`);
    }
  };

  const handleUpdateAdditionalDetails = async () => {
    try {
      const payload = {
        referenceNumber: additionalDetailsState.referenceNumber,
        passportNumber: additionalDetailsState.passportNumber,
        dateOfBirth: additionalDetailsState.dateOfBirth
          ? moment(additionalDetailsState.dateOfBirth).format('YYYY-MM-DD')
          : null,
        trackingLink: additionalDetailsState.trackingLink,
      };
      const res = await callApi(
        `${PARTNER_MEMBER_ADDITIONAL_DETAILS}/${memberData.id}`,
        'PATCH',
        payload
      );
      if (res?.status || res?.data?.statusCode === 200) {
        toastSuccess('Additional details updated successfully');
        setMemberData({ ...memberData, ...payload });
      } else {
        toastError('Failed to update additional details');
      }
    } catch (error) {
      toastError('Failed to update additional details');
    }
  };

  const handleDocumentAcceptReject = async (documentId: string, approvedStatus: boolean) => {
    try {
      const groupId = memberData?.group?.id;
      if (!groupId) {
        toastError('Group ID not found');
        return;
      }

      const res = await callApi(
        `${PARTNER_DOCUMENT_ACCEPT_REJECT}/${groupId}/${documentId}`,
        'PATCH',
        { approved_status: approvedStatus }
      );

      if (res?.data?.statusCode === 200 || res?.status) {
        if (approvedStatus) {
          toastSuccess('Document Accepted');
          // Update background color to green (same as Documitra Order)
          const acceptCard = document.getElementById(`${documentId}-accept-reject`);
          if (acceptCard) {
            acceptCard.style.backgroundColor = 'lightgreen';
          }
        } else {
          toastError('Document Rejected');
          // Update background color to red/salmon (same as Documitra Order)
          const rejectCard = document.getElementById(`${documentId}-accept-reject`);
          if (rejectCard) {
            rejectCard.style.backgroundColor = 'salmon';
          }
        }

        // Update local state
        const updatedDocs = memberData.documents?.map((doc: any) =>
          doc.id === documentId ? { ...doc, status: approvedStatus ? 'accepted' : 'rejected' } : doc
        );
        setMemberData({ ...memberData, documents: updatedDocs });
      } else {
        toastError('Failed to update document status');
      }
    } catch (error: any) {
      console.error('Error accepting/rejecting document:', error);
      toastError(error?.response?.data?.message || 'Failed to update document status');
    }
  };

  const handleUploadProcessedDoc = (e: any) => {
    const files = Array.from(e.target.files as FileList);
    if (files.length === 0) return;

    // Verify only PDFs are staged
    const pdfFiles = files.filter(
      file => file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );
    if (pdfFiles.length < files.length) {
      toastError('Only PDF files are allowed');
    }

    if (pdfFiles.length > 0) {
      setStagedFiles(prev => [...prev, ...pdfFiles]);
      toastSuccess(`${pdfFiles.length} file(s) staged for upload`);
    }

    // Reset input
    e.target.value = '';
  };

  const handleUpdateAllPDFs = async () => {
    if (stagedFiles.length === 0) {
      toastError('No new files to upload');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    const newUploadedDocs = [];

    try {
      for (const file of stagedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('memberId', memberData.id);
        formData.append('documentType', 'Processed Document');

        const res: any = await uploadImageApiCall('/partner-portal/document/upload', formData);

        if (res?.isSuccess || res?.status === 201 || res?.status === 200) {
          const newDoc = res.data?.data || res.data;
          newUploadedDocs.push(newDoc);
          successCount++;
        }
      }

      if (successCount > 0) {
        toastSuccess(`${successCount} document(s) uploaded successfully`);
        const updatedDocs = [...(memberData.processedDocuments || []), ...newUploadedDocs];
        setMemberData({ ...memberData, processedDocuments: updatedDocs });
        setStagedFiles([]); // Clear staging
      } else {
        toastError('Upload failed');
      }
    } catch (error) {
      console.error('Batch upload error:', error);
      toastError('An error occurred during upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteProcessedDoc = async (docId: string) => {
    try {
      const res = await callApi(`${PARTNER_ORDER_PROCESSED_PDF}/${docId}`, 'DELETE');
      if (res?.status) {
        toastSuccess('Document deleted');
        const updatedDocs = memberData.processedDocuments.filter((d: any) => d.id !== docId);
        setMemberData({ ...memberData, processedDocuments: updatedDocs });
      }
    } catch (error) {
      toastError('Failed to delete document');
    }
  };

  const escapeHtml = (value: any) =>
    String(value ?? '--')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const formatDateValue = (d: any) => {
    if (!d) return '—';
    const sd = new Date(d);
    if (isNaN(sd.getTime())) return '—';
    const day = sd.getDate();
    const monthName = sd.toLocaleString('default', { month: 'short' });
    const year = sd.getFullYear();
    return `${day}/${monthName}/${year}`;
  };

  const handleDownloadForm = () => {
    const formDataEntry = memberData?.formEntries?.[0];
    if (!formDataEntry || !formDataEntry.formData) {
      toastError('No form data available to download');
      return;
    }

    const memberForm = formDataEntry.formData;
    const { steps } = memberForm;

    const group = memberData?.group;
    const applyingFrom = group?.applyingFromCountry;
    const citizenship = memberData?.citizenship;
    const destination = memberData?.destination;
    const packageName = group?.partnerPackage?.package?.name;
    const serviceName = group?.partnerPackage?.name;
    const NAVY = 'black';

    const hasTopFields = applyingFrom || citizenship || destination || serviceName;

    const renderPrintQaValue = (question: any) => {
      const inputType = question?.question?.inputType;
      const value = question?.question?.value;
      const url = value?.url;

      if (inputType === 'file upload' || inputType === 'image upload') {
        const fullUrl = url?.startsWith('https') || !url ? url : `${BASE_IMAGE_URL}/${url}`;
        return `<img style="width: 200px; border-radius: 6px; border: 1px solid #e2e8f0;" src="${escapeHtml(fullUrl)}" alt="Image">`;
      }

      if (inputType === 'date') {
        return formatDateValue(value);
      }

      return escapeHtml(value || '—');
    };

    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.write(`<html><head><title>Form Details</title>
        <style>
          @page { size: A4; margin: 15mm 12mm; }
          body { margin: 0; padding: 0; font-family: Inter, sans-serif; background: #fff; color: #0f172a; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          * { box-sizing: border-box; }
          .print-template { width: 100%; }
          .order-section { width: 100%; border: 1px solid #e2e8f0; border-radius: 10px; background: #fff; padding: 18px 20px; margin-bottom: 18px; page-break-inside: avoid; }
          .section-title { font-size: 16px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: ${NAVY}; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; line-height: 1.2; }
          .field-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); column-gap: 28px; row-gap: 14px; }
          .print-field { display: flex; flex-direction: column; gap: 3px; }
          .print-field-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #53657f; line-height: 1.2; }
          .print-field-value { font-size: 14px; font-weight: 700; color: ${NAVY}; line-height: 1.35; word-break: break-word; }
          .dashed-divider { margin-top: 16px; padding-top: 16px; border-top: 1px dashed #cbd5e1; }
          .step-section { width: 100%; background: #fff; margin-bottom: 18px; padding: 0 20px; page-break-inside: avoid; }
          .step-header { font-size: 15px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: ${NAVY}; margin-bottom: 14px; padding-bottom: 10px; line-height: 1.2; }
          .qa-row { display: flex; flex-direction: column; gap: 4px; padding: 10px 0; border-bottom: 1px solid #f1f5f9; align-items: flex-start; }
          .qa-row:last-child { border-bottom: none; }
          .qa-q-prefix { color: ${NAVY}; font-weight: 600; }
          .qa-label { font-size: 12px; font-weight: 500; color: #475569; line-height: 1.5; padding-top: 2px; word-break: break-word; }
          .qa-value { font-size: 12px; font-weight: 600; color: #0f172a; line-height: 1.5; word-break: break-word; }
          .qa-value img { page-break-inside: avoid; }
        </style>
        </head>
        <body>
        <div class="print-template">`);

      printWindow.document.write(`
        <div class="order-section">
          <div class="section-title">Order &amp; Member Information</div>
          <div class="field-grid">
            <div class="print-field"><span class="print-field-label">Order ID</span><span class="print-field-value">${escapeHtml(group?.groupCode || '—')}</span></div>
            <div class="print-field"><span class="print-field-label">Member Name</span><span class="print-field-value">${escapeHtml(memberData?.name || '—')}</span></div>
            <div class="print-field"><span class="print-field-label">Age Group</span><span class="print-field-value">${escapeHtml(memberData?.ageGroup || '—')}</span></div>
            <div class="print-field"><span class="print-field-label">Package Name</span><span class="print-field-value">${escapeHtml(packageName || '—')}</span></div>
          </div>
          ${
            hasTopFields
              ? `
            <div class="dashed-divider"></div>
            <div class="field-grid">
              ${applyingFrom ? `<div class="print-field"><span class="print-field-label">Applying From</span><span class="print-field-value">${escapeHtml(applyingFrom)}</span></div>` : ''}
              ${citizenship ? `<div class="print-field"><span class="print-field-label">Citizenship</span><span class="print-field-value">${escapeHtml(citizenship)}</span></div>` : ''}
              ${destination ? `<div class="print-field"><span class="print-field-label">Destination</span><span class="print-field-value">${escapeHtml(destination)}</span></div>` : ''}
              ${serviceName ? `<div class="print-field"><span class="print-field-label">Service Name</span><span class="print-field-value">${escapeHtml(serviceName)}</span></div>` : ''}
            </div>
          `
              : ''
          }
        </div>
      `);

      if (steps && steps.length > 0) {
        steps.forEach((step: any, stepIdx: number) => {
          const questions = step?.stepQuestions || [];

          const qaRows = questions
            .map((question: any, qIdx: number) => {
              const isLast = qIdx === questions.length - 1;
              return `
              <div class="qa-row" style="${isLast ? 'border-bottom: none;' : ''}">
                <div class="qa-label"><span class="qa-q-prefix">Q</span> ${escapeHtml(question?.question?.label || '—')}</div>
                <div class="qa-value">${renderPrintQaValue(question)}</div>
              </div>
            `;
            })
            .join('');

          printWindow.document.write(`
          <div class="step-section">
            <div class="step-header">${String(stepIdx + 1).padStart(2, '0')}. ${escapeHtml(step?.stepTitle || '')}</div>
            <div>${qaRows}</div>
          </div>
        `);
        });
      } else {
        printWindow.document.write(
          '<div style="padding: 20px; color: #64748b;">No form data available for this member.</div>'
        );
      }

      printWindow.document.write(`
        </div>
        </body>
        </html>
      `);

      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 400);
    }
  };

  const partnerDetails = {
    id: memberData?.partner?.id || '1',
    name: memberData?.partner?.partnerName || memberData?.partner?.name || 'Partner',
    email: memberData?.partner?.email || '--',
    phone: memberData?.partner?.mobileNumber || '--',
  };

  const paymentObj = {
    id: memberData?.id,
    orderId: memberData?.group?.groupCode,
    trackingId: memberData?.trackingLink,
    packageName:
      memberData?.group?.partnerPackage?.package?.name || memberData?.group?.partnerPackage?.name,
    pricingPlan: {
      title: memberData?.group?.partnerPackage?.name,
      discountedPrice: '--',
    },
    paymentDate: memberData?.createdAt ? new Date(memberData.createdAt).toLocaleDateString() : '--',
    paymentDetails: {
      paymentStatus: memberData?.status,
      provider: 'Partner Wallet',
      amountPaid: '--',
    },
    photoEnhancement: {},
    expedite: {},
    apostilled: {},
  };

  const {
    showOnlyForm,
    showOnlyDocuments,
    showOnlyProcessed,
    showOnlyAdditionalDetails,
    hideApplicationStatus,
  } = props;

  const getDefaultMemberStatus = () => {
    const status = memberData?.status;
    if (status) {
      const found = partnerMemberStatusOptions.find(
        (o: any) => o.value === status || o.value?.toLowerCase() === status?.toLowerCase()
      );
      return found ? found.value : status;
    }

    // Derive default status if none exists in backend
    const formDataEntry = memberData?.formEntries?.[0];
    const steps = formDataEntry?.formData?.steps;

    if (!steps) {
      // No form steps = form not filled yet
      return 'Order Pending';
    }

    // Form filled but no status from backend (unlikely but possible)
    return 'Document Pending';
  };
  const [memberStatus, setMemberStatus] = useState<string>(getDefaultMemberStatus());

  useEffect(() => {
    setMemberStatus(getDefaultMemberStatus());
  }, [memberData?.status]);

  const handleMemberStatusUpdateFromForm = async () => {
    try {
      const res = await callApi(`/partner-portal/members/${memberData.id}/status`, 'PATCH', {
        status: memberStatus,
      });
      if (res?.data?.statusCode === 200 || res?.status) {
        toastSuccess('Member status updated successfully');
        setMemberData({ ...memberData, status: memberStatus });
        props.onMemberStatusUpdated?.(memberData.id, memberStatus);
      } else {
        toastError('Failed to update member status');
      }
    } catch (error: any) {
      console.error('Error updating member status:', error);
      toastError(error?.response?.data?.message || 'Failed to update member status');
    }
  };

  const getAdditionalDetailsForm = () => (
    <div
      style={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: '20px',
        margin: '25px 0 0 0',
      }}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <label
          style={{
            fontWeight: 600,
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#64748b',
            margin: 0,
          }}
        >
          Reference Number:
        </label>
        <Input
          type="text"
          value={additionalDetailsState.referenceNumber}
          onChange={e =>
            setAdditionalDetailsState({
              ...additionalDetailsState,
              referenceNumber: e.target.value,
            })
          }
          style={{
            width: '100%',
            height: '36px',
            borderRadius: '6px',
          }}
        />
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <label
          style={{
            fontWeight: 600,
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#64748b',
            margin: 0,
          }}
        >
          Date of Birth:
        </label>
        <div
          onPaste={(event: any) => {
            const pastedValue = event.clipboardData?.getData('text')?.trim();
            const pastedDate = moment(pastedValue, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);
            if (pastedDate.isValid()) {
              event.preventDefault();
              setAdditionalDetailsState({ ...additionalDetailsState, dateOfBirth: pastedDate });
            }
          }}
        >
          <DatePicker
            value={additionalDetailsState.dateOfBirth}
            onChange={date =>
              setAdditionalDetailsState({ ...additionalDetailsState, dateOfBirth: date })
            }
            format={['DD/MM/YYYY', 'YYYY-MM-DD']}
            placeholder="DD/MM/YYYY"
            style={{
              width: '100%',
              height: '36px',
              borderRadius: '6px',
            }}
          />
        </div>
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <label
          style={{
            fontWeight: 600,
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#64748b',
            margin: 0,
          }}
        >
          Passport Number:
        </label>
        <Input
          type="text"
          value={additionalDetailsState.passportNumber}
          onChange={e =>
            setAdditionalDetailsState({
              ...additionalDetailsState,
              passportNumber: e.target.value,
            })
          }
          style={{
            width: '100%',
            height: '36px',
            borderRadius: '6px',
          }}
        />
      </div>
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <label
          style={{
            fontWeight: 600,
            fontSize: '13px',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            color: '#64748b',
            margin: 0,
          }}
        >
          Tracking Link:
        </label>
        <Input
          type="text"
          value={additionalDetailsState.trackingLink}
          onChange={e =>
            setAdditionalDetailsState({
              ...additionalDetailsState,
              trackingLink: e.target.value,
            })
          }
          style={{
            width: '100%',
            height: '36px',
            borderRadius: '6px',
          }}
        />
      </div>
      <InvertFillButton
        variant="blue"
        size="sm"
        fontSize="base"
        fontWeight="medium"
        onClick={handleUpdateAdditionalDetails}
        style={{ width: 'fit-content', gridColumn: '1 / -1', marginTop: '5px' }}
      >
        Update
      </InvertFillButton>
    </div>
  );

  // If specific sections are requested, render only those
  if (showOnlyAdditionalDetails) {
    return <div style={{ padding: 0 }}>{getAdditionalDetailsForm()}</div>;
  }

  if (showOnlyForm) {
    const downloadBtn = (
      <InvertFillButton
        variant="blue"
        size="sm"
        fontSize="base"
        fontWeight="medium"
        onClick={handleDownloadForm}
      >
        <Download size={16} /> Download Form
      </InvertFillButton>
    );
    return <>{getMemberFormData(downloadBtn)}</>;
  }

  if (showOnlyDocuments) {
    return getMemberDocuments();
  }

  if (showOnlyProcessed) {
    const totalDocs = (memberData?.processedDocuments?.length || 0) + stagedFiles.length;
    const hasAnyDocs = totalDocs > 0;

    if (hasAnyDocs) {
      return (
        <div>
          <Text
            style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '15px',
              display: 'block',
            }}
          >
            {totalDocs} document(s) uploaded
            {stagedFiles.length > 0 && (
              <Text type="warning" style={{ marginLeft: '10px' }}>
                ({stagedFiles.length} unsaved)
              </Text>
            )}
          </Text>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px',
              marginBottom: '15px',
            }}
          >
            {memberData?.processedDocuments?.map((doc: any, idx: number) => {
              const isAccepted = doc.status === true || doc.status === 'accepted';
              const isRejected = doc.status === false || doc.status === 'rejected';
              const docUrl = doc.url || doc.s3Key;

              return (
                <div
                  key={doc.id || `existing-${idx}`}
                  style={{
                    position: 'relative',
                    borderRadius: '8px',
                    padding: '10px',
                    backgroundColor: isAccepted
                      ? '#90EE90'
                      : isRejected
                        ? '#fb8775'
                        : doc?.remark
                          ? '#fffbe6'
                          : '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '280px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '16px',
                      left: '16px',
                      zIndex: 1,
                      padding: '4px 9px',
                      borderRadius: '4px',
                      backgroundColor: isAccepted ? '#28a745' : isRejected ? '#dc3545' : '#53657f',
                      color: '#fff',
                      fontSize: '10px',
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {isAccepted ? 'Accepted' : isRejected ? 'Rejected' : 'Pending'}
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '200px',
                      marginBottom: '10px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      backgroundColor: '#f8fafc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PdfViewer photo={docUrl} fileName={doc.name} isSmallPreview={true} />
                  </div>
                  <div className="processed_data_text" style={{ flex: 1 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '4px',
                        gap: '8px',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: '15px',
                          color: '#666',
                          wordBreak: 'break-word',
                          flex: 1,
                          lineHeight: '1.2',
                        }}
                      >
                        {doc?.name || 'Untitled'}
                      </Text>
                      <div
                        style={{
                          display: 'flex',
                          gap: '6px',
                          flexShrink: 0,
                        }}
                      >
                        <Tooltip title="Delete (Existing)">
                          <DeleteOutlined
                            style={{
                              backgroundColor: '#00000070',
                              padding: '4px',
                              borderRadius: '4px',
                              fontSize: 16,
                              color: 'white',
                              cursor: 'pointer',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={() => handleDeleteProcessedDoc(doc.id)}
                          />
                        </Tooltip>
                        <Tooltip title="Copy Link">
                          <CopyOutlined
                            style={{
                              backgroundColor: '#00000070',
                              borderRadius: '4px',
                              padding: '4px',
                              fontSize: 16,
                              color: 'white',
                              cursor: 'pointer',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={() => {
                              const url = docUrl;
                              if (url) {
                                navigator.clipboard.writeText(url);
                                toastSuccess('Link copied to clipboard');
                              }
                            }}
                          />
                        </Tooltip>
                        <Tooltip title="Download">
                          <DownloadOutlined
                            style={{
                              backgroundColor: '#00000070',
                              borderRadius: '4px',
                              padding: '4px',
                              fontSize: 16,
                              color: 'white',
                              cursor: 'pointer',
                              width: '24px',
                              height: '24px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            onClick={() => onDownload(docUrl)}
                          />
                        </Tooltip>
                      </div>
                    </div>
                    <Text
                      style={{
                        fontSize: '15px',
                        color: '#666',
                        display: 'block',
                        marginBottom: '4px',
                        lineHeight: '1.2',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      Remark: {doc?.remark ? doc?.remark : 'N/A'}
                    </Text>
                  </div>
                </div>
              );
            })}

            {stagedFiles.map((file, idx) => (
              <div
                key={`staged-${idx}`}
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  padding: '10px',
                  backgroundColor: '#e6f7ff',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '280px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #91d5ff',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 1,
                    padding: '4px 9px',
                    borderRadius: '4px',
                    backgroundColor: '#1890ff',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  STAGED
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '200px',
                    marginBottom: '10px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #40a9ff',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <FilePdfOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
                    <div style={{ marginTop: '8px', color: '#40a9ff', fontWeight: 600 }}>
                      New PDF Staged
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Upload to Save</div>
                  </div>
                </div>
                <div className="processed_data_text" style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '4px',
                      gap: '8px',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '15px',
                        color: '#0050b3',
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        flex: 1,
                        lineHeight: '1.2',
                      }}
                    >
                      {file.name}
                    </Text>
                    <Tooltip title="Remove">
                      <X
                        size={24}
                        style={{
                          backgroundColor: '#dc2626',
                          borderRadius: '4px',
                          padding: '4px',
                          color: 'white',
                          cursor: 'pointer',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                        onClick={() => handleRemoveStagedFile(idx)}
                      />
                    </Tooltip>
                  </div>
                  <Tag color="processing" style={{ marginTop: '4px' }}>
                    Ready to upload
                  </Tag>
                </div>
              </div>
            ))}

            <label
              htmlFor={`additional-processed-pdf-${memberData?.id}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '10px',
                minHeight: '280px',
                border: '2px dashed #cbd5e1',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                cursor: 'pointer',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
                userSelect: 'none',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#004195';
                e.currentTarget.style.backgroundColor = '#f0f6ff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.backgroundColor = '#fafafa';
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  border: '2px solid #004195',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#004195',
                  fontSize: '28px',
                  fontWeight: 300,
                  lineHeight: 1,
                }}
              >
                +
              </div>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#004195',
                  letterSpacing: '0.02em',
                }}
              >
                Upload Doc
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8' }}>Click to select a PDF file</div>
            </label>
          </div>
          <input
            onChange={e => handleUploadProcessedDoc(e)}
            type="file"
            id={`additional-processed-pdf-${memberData?.id}`}
            accept="application/pdf"
            style={{ display: 'none' }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'start',
              marginTop: '30px',
              marginBottom: '20px',
            }}
          >
            <button
              onClick={handleUpdateAllPDFs}
              style={{
                backgroundColor: '#004195',
                borderRadius: '6px',
                color: 'white',
                fontWeight: 600,
                border: 'none',
                cursor: stagedFiles.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                height: '40px !important',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                opacity: stagedFiles.length === 0 ? 0.6 : 1,
              }}
              disabled={stagedFiles.length === 0 || isUploading}
            >
              {isUploading && <Spin size="small" style={{ color: 'white' }} />}
              {isUploading ? 'Processing...' : 'Update All PDFs'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
            gap: '12px',
            marginBottom: '15px',
          }}
        >
          <label
            htmlFor={`empty-processed-pdf-${memberData?.id}`}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '10px',
              minHeight: '280px',
              border: '2px dashed #cbd5e1',
              borderRadius: '8px',
              backgroundColor: '#fafafa',
              cursor: 'pointer',
              transition: 'border-color 0.15s ease, background-color 0.15s ease',
              userSelect: 'none',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#004195';
              e.currentTarget.style.backgroundColor = '#f0f6ff';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#cbd5e1';
              e.currentTarget.style.backgroundColor = '#fafafa';
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                border: '2px solid #004195',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#004195',
                fontSize: '28px',
                fontWeight: 300,
                lineHeight: 1,
              }}
            >
              +
            </div>
            <div
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#004195',
                letterSpacing: '0.02em',
              }}
            >
              Upload Doc
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>Click to select a PDF file</div>
          </label>
        </div>
        <input
          onChange={e => handleUploadProcessedDoc(e)}
          type="file"
          id={`empty-processed-pdf-${memberData?.id}`}
          accept="application/pdf"
          style={{ display: 'none' }}
        />
        {stagedFiles.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '12px',
              marginBottom: '15px',
              marginTop: '12px',
            }}
          >
            {stagedFiles.map((file, idx) => (
              <div
                key={`staged-empty-${idx}`}
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  padding: '10px',
                  backgroundColor: '#e6f7ff',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '280px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #91d5ff',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 1,
                    padding: '4px 9px',
                    borderRadius: '4px',
                    backgroundColor: '#1890ff',
                    color: '#fff',
                    fontSize: '10px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  STAGED
                </div>
                <div
                  style={{
                    width: '100%',
                    height: '200px',
                    marginBottom: '10px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed #40a9ff',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <FilePdfOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
                    <div style={{ marginTop: '8px', color: '#40a9ff', fontWeight: 600 }}>
                      New PDF Staged
                    </div>
                    <div style={{ fontSize: '12px', color: '#8c8c8c' }}>Upload to Save</div>
                  </div>
                </div>
                <div className="processed_data_text" style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '4px',
                      gap: '8px',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '15px',
                        color: '#0050b3',
                        fontWeight: 600,
                        wordBreak: 'break-word',
                        flex: 1,
                        lineHeight: '1.2',
                      }}
                    >
                      {file.name}
                    </Text>
                    <Tooltip title="Remove">
                      <X
                        size={24}
                        style={{
                          backgroundColor: '#dc2626',
                          borderRadius: '4px',
                          padding: '4px',
                          color: 'white',
                          cursor: 'pointer',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                        onClick={() => handleRemoveStagedFile(idx)}
                      />
                    </Tooltip>
                  </div>
                  <Tag color="processing" style={{ marginTop: '4px' }}>
                    Ready to upload
                  </Tag>
                </div>
              </div>
            ))}
          </div>
        )}
        {stagedFiles.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'start',
              marginTop: '30px',
              marginBottom: '20px',
            }}
          >
            <button
              onClick={handleUpdateAllPDFs}
              style={{
                backgroundColor: '#004195',
                borderRadius: '6px',
                color: 'white',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                height: '40px !important',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
              disabled={isUploading}
            >
              {isUploading && <Spin size="small" style={{ color: 'white' }} />}
              {isUploading ? 'Processing...' : 'Update All PDFs'}
            </button>
          </div>
        )}
        {!hideApplicationStatus && (
          <div style={{ marginTop: '20px', padding: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <Text style={{ fontSize: '16px', fontWeight: '600', minWidth: '80px' }}>
                Application Status:
              </Text>
              <Select
                style={{ width: 300, fontSize: '14px' }}
                value={memberStatus || ''}
                onChange={value => setMemberStatus(value)}
                placeholder="Select member status"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const label = option?.children;
                  const text = typeof label === 'string' ? label : String(label ?? '');
                  return text.toLowerCase().includes(input.toLowerCase());
                }}
                size="middle"
              >
                {partnerMemberStatusOptions.map((opt: any) => (
                  <Select.Option key={opt.id} value={opt.value}>
                    {opt.name}
                  </Select.Option>
                ))}
              </Select>
              <Button
                type="primary"
                onClick={handleMemberStatusUpdateFromForm}
                disabled={!memberStatus}
                style={{
                  fontSize: '14px',
                  height: '32px',
                  padding: '0 20px',
                  backgroundColor: '#586f80',
                  borderColor: '#586f80',
                  borderRadius: '4px',
                }}
              >
                Update
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full component view (when used standalone)
  return (
    <div style={{ padding: '20px', background: '#f5f7fa' }}>
      {/* Header Title similar to "Document Order <ID>" */}
      <div
        style={{
          background: '#fff',
          padding: '15px 20px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Document Order
        </Title>
        <div
          style={{
            marginLeft: '15px',
            padding: '4px 12px',
            background: '#f0f0f0',
            borderRadius: '4px',
            fontWeight: 'bold',
          }}
        >
          {memberData?.group?.groupCode}
        </div>
      </div>

      <UserDetails userDetails={partnerDetails} />
      <PaymentDetails
        paymentDetails={paymentObj}
        userName={partnerDetails.name}
        userEmail={partnerDetails.email}
        formDetails={memberData}
      />

      <Collapse defaultActiveKey={['1']} style={{ marginBottom: '20px' }}>
        <Panel header="Application Form Data" key="1">
          {getMemberFormData(
            <InvertFillButton
              variant="blue"
              size="sm"
              fontSize="base"
              fontWeight="medium"
              onClick={handleDownloadForm}
            >
              <Download size={16} /> Download Form
            </InvertFillButton>
          )}
        </Panel>
        <Panel header="Uploaded Documents" key="2">
          {getMemberDocuments()}
        </Panel>
        <Panel header="Processed Documents (Admin Upload)" key="3">
          <div style={{ padding: '10px' }}>
            <Text type="secondary" style={{ marginBottom: '10px', display: 'block' }}>
              Admin can upload processed PDFs for the member here.
            </Text>

            <div style={{ marginBottom: '20px' }}>
              <input
                type="file"
                accept=".pdf"
                onChange={e => handleUploadProcessedDoc(e)}
                style={{ marginBottom: '10px' }}
              />
            </div>

            {memberData?.processedDocuments && memberData.processedDocuments.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {memberData.processedDocuments.map((doc: any) => (
                  <div
                    key={doc.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px',
                      border: '1px solid #eee',
                      borderRadius: '4px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Text strong>{doc.name || 'Processed Document'}</Text>
                        <a
                          href={
                            doc.url?.startsWith('https') ? doc.url : `${BASE_IMAGE_URL}/${doc.url}`
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          (View)
                        </a>
                      </div>
                      <Tag
                        color={
                          doc.status === true || doc.status === 'accepted'
                            ? 'success'
                            : doc.status === false || doc.status === 'rejected'
                              ? 'error'
                              : 'default'
                        }
                      >
                        {doc.status === true || doc.status === 'accepted'
                          ? 'Accepted'
                          : doc.status === false || doc.status === 'rejected'
                            ? 'Rejected'
                            : 'Pending Review'}
                      </Tag>
                    </div>
                    <Space>
                      <Tooltip title="Download">
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => onDownload(doc.url)}
                          size="small"
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          danger
                          icon={<CloseCircleOutlined />}
                          size="small"
                          onClick={() => handleDeleteProcessedDoc(doc.id)}
                        />
                      </Tooltip>
                    </Space>
                  </div>
                ))}
              </div>
            ) : (
              <Text>No processed documents uploaded yet.</Text>
            )}
          </div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default ReviewPartnerOrderMember;
